import React, { useState, useEffect, useCallback } from "react";
import CustomModal from "./customModal";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import { useSelector } from "react-redux";
import useThemeColor from "../../../hooks/useThemeColor";
import "./customerGroupSelectionModel.css";

export default function CustomerGroupSelectionModal({
  showModal,
  setShowModal,
  onSubmit,
  selectedGroups,
  setSelectedGroups,
  subtitle = "Select customer groups to apply the discount",
}) {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const themeColor = useThemeColor();
  const [customerGroups, setCustomerGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [tempSelectedGroups, setTempSelectedGroups] = useState(selectedGroups || []);
  const [loading, setLoading] = useState(false);

  const fetchCustomerGroups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CUSTOMERS_GROUP, {
        params: {
          CompID,
          Search: searchTerm || "",
          SortBy: "Name",
          SortOrder: "ASC",
        },
      });

      const groupData = response.data.data || [];
      if (Array.isArray(groupData)) {
        setCustomerGroups(groupData);
        setFilteredGroups(groupData);
      } else {
        setCustomerGroups([]);
        setFilteredGroups([]);
      }
    } catch (error) {
      setCustomerGroups([]);
      setFilteredGroups([]);
    } finally {
      setLoading(false);
    }
  }, [CompID, searchTerm]);

  useEffect(() => {
    if (showModal) {
      fetchCustomerGroups();
    }
  }, [showModal, fetchCustomerGroups]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredGroups(customerGroups);
    } else {
      const filtered = customerGroups.filter((group) =>
        group.name?.toLowerCase().includes(searchTerm.trim().toLowerCase())
      );
      setFilteredGroups(filtered);
    }
  }, [searchTerm, customerGroups]);

  useEffect(() => {
    setTempSelectedGroups(selectedGroups || []);
  }, [selectedGroups, showModal]);

  const handleGroupSelection = (group) => {
    setTempSelectedGroups((prev) => {
      if (prev.some((g) => g.id === group.id)) {
        return prev.filter((g) => g.id !== group.id);
      }
      return [...prev, group];
    });
  };

  const handleSubmit = () => {
    if (typeof onSubmit === "function") {
      onSubmit(tempSelectedGroups);
    }
    setSelectedGroups(tempSelectedGroups);
    setShowModal(false);
  };

  const selectedCount = tempSelectedGroups.length;

  return (
    <CustomModal
      isOpen={showModal}
      title="Select customer groups"
      subtitle={subtitle}
      onClose={() => setShowModal(false)}
      onSubmit={handleSubmit}
      showButton={true}
      showCancelButton={true}
      showSubmitButton={true}
      submitButtonText="Done"
      isCentered={true}
    >
      <div className="cg-select-modal" style={{ "--cg-accent": themeColor }}>
        <div className="cg-select-modal__toolbar">
          <div className="cg-select-modal__search">
            <i className="fa fa-search cg-select-modal__search-icon" aria-hidden="true" />
            <input
              type="text"
              className="cg-select-modal__search-input"
              placeholder="Search customer groups"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search customer groups"
            />
            {searchTerm ? (
              <button
                type="button"
                className="cg-select-modal__search-clear"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            ) : null}
          </div>

          <div className="cg-select-modal__meta">
            <span>
              {loading
                ? "Loading groups…"
                : `${filteredGroups.length} group${filteredGroups.length === 1 ? "" : "s"}`}
            </span>
            {selectedCount > 0 ? (
              <span className="cg-select-modal__count">
                <i className="fa fa-check" aria-hidden="true" />
                {selectedCount} selected
              </span>
            ) : (
              <span>None selected</span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="cg-select-modal__state">
            <div className="cg-select-modal__spinner" aria-hidden="true" />
            <p className="cg-select-modal__state-title">Loading customer groups</p>
            <p className="cg-select-modal__state-text">Please wait a moment…</p>
          </div>
        ) : filteredGroups.length > 0 ? (
          <div className="cg-select-modal__list" role="listbox" aria-multiselectable="true">
            {filteredGroups.map((group) => {
              const isSelected = tempSelectedGroups.some((g) => g.id === group.id);
              return (
                <button
                  key={group.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`cg-select-row${isSelected ? " is-selected" : ""}`}
                  onClick={() => handleGroupSelection(group)}
                >
                  <span className="cg-select-row__icon" aria-hidden="true">
                    <i className="fa fa-users" />
                  </span>
                  <span className="cg-select-row__name" title={group.name}>
                    {group.name}
                  </span>
                  <span className="cg-select-row__check" aria-hidden="true">
                    <i className="fa fa-check" />
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="cg-select-modal__state">
            <div className="cg-select-modal__state-icon" aria-hidden="true">
              <i className="fa fa-users" />
            </div>
            <p className="cg-select-modal__state-title">No customer groups found</p>
            <p className="cg-select-modal__state-text">
              {searchTerm
                ? `Nothing matches “${searchTerm}”. Try a different search.`
                : "Create a customer group first, then come back to assign it."}
            </p>
          </div>
        )}
      </div>
    </CustomModal>
  );
}
